import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Star, Heart } from 'lucide-react';
import { useFavorites } from '../context/FavoritesContext';

const SpaCard = ({ spa, variant = 'default' }) => {
    const { isFavorite, toggleFavorite } = useFavorites();
    const favorite = isFavorite(spa.id);

    const handleFavoriteClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleFavorite(spa.id);
    };

    const renderStars = (rating) => {
        const stars = [];
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 >= 0.5;

        for (let i = 0; i < 5; i++) {
            if (i < fullStars) {
                stars.push(
                    <Star key={i} className="w-4 h-4 fill-[#C8A86B] text-[#C8A86B]" />
                );
            } else if (i === fullStars && hasHalfStar) {
                stars.push(
                    <Star key={i} className="w-4 h-4 fill-[#C8A86B]/50 text-[#C8A86B]" />
                );
            } else {
                stars.push(
                    <Star key={i} className="w-4 h-4 text-[#E0DAD0]" />
                );
            }
        }
        return stars;
    };

    if (variant === 'featured') {
        return (
            <Link 
                to={`/spa/${spa.id}`} 
                className="spa-card group block"
                data-testid={`spa-card-${spa.id}`}
            >
                <div className="relative">
                    <div className="image-zoom aspect-[4/3]">
                        <img
                            src={spa.images[0]}
                            alt={spa.name}
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <button
                        onClick={handleFavoriteClick}
                        className={`absolute top-4 right-4 p-2.5 rounded-full transition-all duration-200 ${
                            favorite
                                ? 'bg-[#C8A86B] text-white'
                                : 'bg-white/90 text-[#4A4E4D] hover:bg-[#C8A86B] hover:text-white'
                        }`}
                        data-testid={`favorite-btn-${spa.id}`}
                    >
                        <Heart className={`w-4 h-4 ${favorite ? 'fill-current' : ''}`} />
                    </button>
                    <div className="absolute bottom-4 left-4">
                        <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-[#4A4E4D] text-xs font-medium rounded-full">
                            {spa.spa_type}
                        </span>
                    </div>
                </div>
                <div className="p-5">
                    <h3 className="font-heading text-xl text-[#4A4E4D] mb-2 group-hover:text-[#C8A86B] transition-colors">
                        {spa.name}
                    </h3>
                    <div className="flex items-center gap-1.5 text-[#4A4E4D]/60 text-sm mb-3">
                        <MapPin className="w-4 h-4" />
                        <span>{spa.area}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="flex">{renderStars(spa.rating)}</div>
                            <span className="text-sm text-[#4A4E4D]/60">
                                ({spa.reviews_count})
                            </span>
                        </div>
                        <span className="text-[#C8A86B] font-medium">{spa.price_range}</span>
                    </div>
                </div>
            </Link>
        );
    }

    return (
        <Link 
            to={`/spa/${spa.id}`} 
            className="spa-card group flex flex-col sm:flex-row"
            data-testid={`spa-card-${spa.id}`}
        >
            <div className="relative sm:w-64 flex-shrink-0">
                <div className="image-zoom aspect-[4/3] sm:aspect-square sm:h-full">
                    <img
                        src={spa.images[0]}
                        alt={spa.name}
                        className="w-full h-full object-cover"
                    />
                </div>
                <button
                    onClick={handleFavoriteClick}
                    className={`absolute top-3 right-3 p-2 rounded-full transition-all duration-200 ${
                        favorite
                            ? 'bg-[#722F37] text-white'
                            : 'bg-white/90 text-[#4A4E4D] hover:bg-[#722F37] hover:text-white'
                    }`}
                    data-testid={`favorite-btn-${spa.id}`}
                >
                    <Heart className={`w-4 h-4 ${favorite ? 'fill-current' : ''}`} />
                </button>
                {/* Distance badge */}
                {spa.distance !== undefined && (
                    <div className="absolute bottom-3 left-3 px-2 py-1 bg-[#722F37] text-white text-xs font-medium rounded-full hidden sm:block">
                        {spa.distance.toFixed(1)} km away
                    </div>
                )}
            </div>
            <div className="flex-1 p-5">
                <div className="flex items-start justify-between gap-4 mb-2">
                    <div>
                        <span className="inline-block px-2.5 py-0.5 bg-[#F5E1E4] text-[#722F37] text-xs font-medium rounded-full mb-2">
                            {spa.spa_type}
                        </span>
                        <h3 className="font-heading text-xl text-[#4A4E4D] group-hover:text-[#722F37] transition-colors">
                            {spa.name}
                        </h3>
                    </div>
                    <span className="text-[#C8A86B] font-semibold whitespace-nowrap">{spa.price_range}</span>
                </div>
                <div className="flex items-center gap-1.5 text-[#4A4E4D]/60 text-sm mb-1">
                    <MapPin className="w-4 h-4" />
                    <span>{spa.area}, Bangalore</span>
                </div>
                <p className="text-[#4A4E4D]/50 text-xs mb-3">{spa.address}</p>
                <p className="text-[#4A4E4D]/70 text-sm line-clamp-2 mb-4">
                    {spa.description}
                </p>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="flex">{renderStars(spa.rating)}</div>
                        <span className="text-sm font-medium text-[#4A4E4D]">{spa.rating}</span>
                        <span className="text-sm text-[#4A4E4D]/50">
                            ({spa.reviews_count} reviews)
                        </span>
                    </div>
                    <span className="text-sm text-[#4A4E4D]/60">{spa.opening_hours}</span>
                </div>
            </div>
        </Link>
    );
};

export default SpaCard;
